import { useMemo } from 'react';
import {
  useBotKnowledge,
  useSupportRequests,
} from './useEngageQueries';

/**
 * Aggregate "work-to-do" signal across the workspace.
 *
 * Pulled from the same React Query caches the rest of the app already
 * reads (support requests, bot knowledge), so this hook does NOT trigger
 * any new network round-trips — it just derives counts from lists the
 * sidebar / dashboard have already fetched. Refetches happen on focus
 * (React Query default) so an agent coming back to the tab sees fresh
 * counts within a beat.
 *
 * History (2026-05-24): Conversations was demoted from the top-level
 * sidebar, so `unreadConversations` was dropped from this hook. Bot-only
 * chats now surface as the "Bot activity" section on the Chatbot
 * Insights tab; customer-level history lives on the Customer profile.
 * Nothing in the topbar / sidebar / doc title needs the unread count
 * anymore.
 */
export interface InboxCounts {
  /** Customer-raised support requests still awaiting a first reply. */
  pendingSupport: number;
  /** Bot KB entries pending tenant approval. */
  draftKnowledge: number;
  /** Sum of the above — used to badge the document title. */
  total: number;
}

const EMPTY: InboxCounts = {
  pendingSupport: 0,
  draftKnowledge: 0,
  total: 0,
};

export function useEngageInboxCounts(): InboxCounts {
  const supportQuery = useSupportRequests();
  const knowledgeQuery = useBotKnowledge();

  return useMemo(() => {
    const support = supportQuery.data ?? [];
    const knowledge = knowledgeQuery.data ?? [];

    const pendingSupport = support.filter((r) => r.status === 'pending').length;
    const draftKnowledge = knowledge.filter((k) => k.status === 'draft').length;

    const total = pendingSupport + draftKnowledge;

    // Avoid re-allocating the empty object when everything is zero so
    // downstream consumers with shallow deps don't churn unnecessarily.
    if (total === 0) return EMPTY;

    return { pendingSupport, draftKnowledge, total };
  }, [supportQuery.data, knowledgeQuery.data]);
}
